import { inRange } from '@vividseats/vivid-ui-kit/dist/components/slider/component'
import dayjs from 'dayjs'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'

import {
    PRODUCTION_FILTER_LOCALSTORAGE_KEY,
    ProductionFilterStateLocalStorage,
} from '@/components/pages/production/types'
import type { GlobalPriceRangeObject } from '@/context/listings/types'
import type { ComposedListing } from '@/types/app-types'
import { getHighestFilterPrice } from '@/utils/filters'

import { defaultState as defaultFilterState } from '.'
import { FilterFields, ProductionFilterState } from './types'

export const FILTER_EXPIRATION_HRS = 18

export const naturalSort = (a: string, b: string, shouldSortByStringLength?: boolean) => {
    // both are string
    if (isNaN(Number(a)) && isNaN(Number(b))) {
        // handle rows with double and triple letters i.e., Z should be before AA, ZZ should be before AAA
        if (shouldSortByStringLength) {
            if (a.length > b.length) {
                return 1
            } else if (a.length < b.length) {
                return -1
            }
        }
        return a < b ? -1 : a == b ? 0 : 1
    }
    // only a is a string
    else if (isNaN(Number(a))) return 1
    // only b is a string
    else if (isNaN(Number(b))) return -1
    // both are num
    else return Number(a) - Number(b)
}

export const getHighestAndLowestPriceByShowAip = (globalPriceRangeObject: GlobalPriceRangeObject, isAip: boolean) => {
    return {
        highestPrice: getHighestFilterPrice(globalPriceRangeObject, isAip),
        lowestPrice: isAip ? globalPriceRangeObject.lowestAllInPrice : globalPriceRangeObject.lowestPrice,
    }
}

export const filterListings = (
    listings: ComposedListing[],
    state: ProductionFilterState,
    priceRangeObject: GlobalPriceRangeObject,
): ComposedListing[] => {
    let filteredListings = [...listings]

    const { priceFilterState, quantityFilterState, typeFilterState } = state
    const { greatDeal, instantDelivery, superSeller, recentlyViewed } = typeFilterState
    const hasQuantityFilter = quantityFilterState.quantity !== 0

    // filter logic -- use state to know which filters to apply
    if (greatDeal || instantDelivery || superSeller || recentlyViewed || hasQuantityFilter) {
        filteredListings = filteredListings.filter((listing) => {
            // show only great deals
            if (greatDeal) {
                if (!listing.isGreatDeal) return false
            }

            // show only instant delivery
            if (instantDelivery) {
                if (!listing.isInstantDelivery) return false
            }

            // show only listings from super sellers
            if (superSeller) {
                if (!listing.isPreferredSeller) return false
            }

            // show only recently viewed
            if (recentlyViewed) {
                if (!listing.isRecentlyViewed) return false
            }

            // show only listings where user can buy the quantity they've selected
            if (hasQuantityFilter) {
                if (quantityFilterState.quantity !== 8) {
                    if (!listing.quantityOptionsList.includes(quantityFilterState.quantity)) return false
                } else {
                    if (!listing.quantityOptionsList.some((quantity) => quantity >= 8)) return false
                }
            }

            return true
        })
    }

    // Price Filters
    if (!!priceRangeObject && priceFilterState.currentMin !== 0 && priceFilterState.currentMax !== 0) {
        const { highestPrice: highestFilterPrice, lowestPrice: lowestFilterPrice } = getHighestAndLowestPriceByShowAip(
            priceRangeObject,
            state.showAllInPrice,
        )

        /**
         * This is to allow the inputs to be empty. See the `price-slider` component for more.
         * Range min/max needs smallest/largest values to ensure our filteredListings array is filtered appropriately.
         */
        const rangeMin = priceFilterState.currentMin === Infinity ? 0 : priceFilterState.currentMin

        // If min is altered at all, `priceFilterState.currentMax` will no longer be -Infinity, and set to the highestFilterPrice.
        // This 2nd check ensures that expensive tickets past getHighestFilterPrice will still show up.
        const rangeMax =
            priceFilterState.currentMax === -Infinity || priceFilterState.currentMax >= highestFilterPrice
                ? Number.MAX_SAFE_INTEGER
                : priceFilterState.currentMax

        const range = [rangeMin, rangeMax]
        if (priceFilterState.currentMin > lowestFilterPrice || priceFilterState.currentMax < highestFilterPrice) {
            filteredListings = filteredListings.filter((listing) => {
                const listingPrice = state.showAllInPrice ? listing.allInPrice ?? 0 : listing.price
                const revisedListingPrice = listing.shouldUseLocalPrice ? Math.ceil(listingPrice) : listingPrice

                return inRange(revisedListingPrice, range)
            })
        }
    }

    return filteredListings
}

export const omitExpiredProductionFilters = (
    payload: ProductionFilterStateLocalStorage,
): ProductionFilterStateLocalStorage => {
    const result = cloneDeep(payload)

    for (const productionId in result) {
        if (result[productionId].expiration <= Date.now()) {
            delete result[productionId]
        }
    }

    return result
}

export const getProductionFilterLocalStorage = (): ProductionFilterStateLocalStorage => {
    try {
        const rawStoredFilter = localStorage.getItem(PRODUCTION_FILTER_LOCALSTORAGE_KEY) ?? '{}'
        const result = JSON.parse(rawStoredFilter)
        return omitExpiredProductionFilters(result)
    } catch (e) {
        console.error('JSON parse error when parsing filter data from localStroage', e)
        localStorage.removeItem(PRODUCTION_FILTER_LOCALSTORAGE_KEY)
        return {}
    }
}

export const setProductionFilterLocalStorage = (payload: ProductionFilterStateLocalStorage | null) => {
    if (!payload) return
    try {
        localStorage.setItem(PRODUCTION_FILTER_LOCALSTORAGE_KEY, JSON.stringify(payload))
    } catch (e) {
        console.error('Invalid Local Storage value, filter not parsed at this time', e)
    }
}

export const setProductionFilterLocalStorageById = (
    currentProductionId: number,
    payload: ProductionFilterState,
    globalPriceRangeObject: GlobalPriceRangeObject,
    showAip: boolean,
) => {
    const { highestPrice, lowestPrice } = getHighestAndLowestPriceByShowAip(globalPriceRangeObject, showAip)

    const shouldApplyMaxPrice = payload.priceFilterState.currentMax < highestPrice
    const shouldApplyMinPrice = payload.priceFilterState.currentMin > lowestPrice

    const currentParsedStoredFilter = getProductionFilterLocalStorage()
    const savedProduction = currentParsedStoredFilter[currentProductionId]
    const savedCurrency = savedProduction?.priceFilterState.currency
    const savedMinPrice = savedProduction?.priceFilterState.currentMin
    const savedMaxPrice = savedProduction?.priceFilterState.currentMax

    const hasCurrencyChanged = savedCurrency !== payload.priceFilterState.currency

    let filtersUnchanged = isEqual(omit(savedProduction, ['priceFilterState']), omit(payload, ['priceFilterState']))

    if (savedMinPrice !== undefined) {
        filtersUnchanged &&= savedMinPrice === payload.priceFilterState.currentMin
    }

    if (savedMaxPrice !== undefined) {
        filtersUnchanged &&= savedMaxPrice === payload.priceFilterState.currentMax
    }

    const resultFilterToBeStored: ProductionFilterStateLocalStorage = {
        ...currentParsedStoredFilter,
        [currentProductionId]: {
            ...savedProduction,
            ...payload,
            priceFilterState: {
                ...((shouldApplyMaxPrice || hasCurrencyChanged) && { currentMax: payload.priceFilterState.currentMax }),
                ...((shouldApplyMinPrice || hasCurrencyChanged) && { currentMin: payload.priceFilterState.currentMin }),
                currency: hasCurrencyChanged ? payload.priceFilterState.currency : savedCurrency,
            },
            ...(!filtersUnchanged && { expiration: dayjs().add(FILTER_EXPIRATION_HRS, 'h').valueOf() }),
        },
    }
    setProductionFilterLocalStorage(resultFilterToBeStored)
}

export const checkModifiedFilter = (
    filterState: ProductionFilterState,
    highPrice: number,
    lowPrice: number,
    showAip: boolean,
    checkSelectedSeatsFilter = false,
): FilterFields[] => {
    const modifiedFilterList = []

    if (defaultFilterState.sortFilterState.name !== filterState.sortFilterState.name) {
        modifiedFilterList.push(FilterFields.Type)
    }

    if (defaultFilterState.quantityFilterState.quantity !== filterState.quantityFilterState.quantity) {
        modifiedFilterList.push(FilterFields.Quantity)
    }

    if (showAip !== filterState.showAllInPrice) {
        modifiedFilterList.push(FilterFields.AllInPrice)
    }

    if (filterState.priceFilterState.currentMax !== highPrice || filterState.priceFilterState.currentMin !== lowPrice) {
        modifiedFilterList.push(FilterFields.PriceRange)
    }

    if (
        defaultFilterState.typeFilterState.superSeller !== filterState.typeFilterState.superSeller ||
        defaultFilterState.typeFilterState.greatDeal !== filterState.typeFilterState.greatDeal ||
        defaultFilterState.typeFilterState.instantDelivery !== filterState.typeFilterState.instantDelivery
    ) {
        modifiedFilterList.push(FilterFields.TicketType)
    }

    if (checkSelectedSeatsFilter && filterState.selectedSectionsFilterState.length > 0) {
        modifiedFilterList.push(FilterFields.Seat)
    }

    if (filterState.selectedRowsFilterState.length) {
        modifiedFilterList.push(FilterFields.Seat)
    }

    return modifiedFilterList
}
